/**
 * A batch is the embryos created together in one cycle, from fresh eggs at a
 * retrieval or from frozen or donor eggs thawed and fertilized later. The
 * Embryo testing page groups reports by batch and dates each batch by when its
 * embryos were created, not by when the eggs were retrieved.
 */
import type { EmbryoReport } from "@src/packages/cycles/hooks/useEmbryoReport";
import type { Specimen } from "../useSpecimens";

type BatchReport = Pick<EmbryoReport, "retrievalDate"> & {
  embryos: readonly Pick<EmbryoReport["embryos"][number], "embryoSpecimenId">[];
};
type EmbryoRecord = Pick<Specimen, "id" | "collectedAt">;

/**
 * When a report's embryos were created: the earliest date the cryo vault
 * records for the embryos the report links to. Embryos made from frozen eggs
 * are created long after the eggs were retrieved, so the report's retrieval
 * date is only the fallback, for embryos without a vault record. For fresh
 * eggs the two are the same day.
 */
export function getEmbryoBatchDate(
  report: BatchReport,
  specimens: readonly EmbryoRecord[],
): string | null {
  const linkedIds = new Set(
    report.embryos
      .map((embryo) => embryo.embryoSpecimenId)
      .filter((id): id is string => Boolean(id)),
  );
  const created = specimens
    .filter((specimen) => linkedIds.has(specimen.id) && specimen.collectedAt)
    .map((specimen) => specimen.collectedAt.slice(0, 10))
    .sort();
  return created[0] ?? (report.retrievalDate || null);
}

/** Reports newest batch first, so the first one is the latest batch. */
export function sortReportsByBatchDate<Report extends BatchReport>(
  reports: readonly Report[],
  specimens: readonly EmbryoRecord[],
): Report[] {
  return reports
    .map((report) => ({
      report,
      date: getEmbryoBatchDate(report, specimens) ?? "",
    }))
    .sort((a, b) => b.date.localeCompare(a.date))
    .map(({ report }) => report);
}
